import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
const apiUrl = process.env.REACT_APP_API_URL;
function AdminEditStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    studentid: '',
    firstName: '',
    lastName: '',
    aadharNo: '',
    mobile: '',
    email: '',
    address: '',
    postcode: '',
    mainTelephone: '',
    degree: '',
    engineering: '',
    currentStatus: '',
    preferableCourse: '',
    batch: '',
    username: '',
    password: '',
    status: 1,
    decryptedPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [allocationNotice, setAllocationNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  useEffect(() => {
    const fetchStudent = async () => {
      setIsLoading(true);
      setApiError('');
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        if (!sessionData || !sessionData.token) {
          setApiError('No authentication token found. Please log in.');
          navigate('/admin-login');
          return;
        }
        const response = await axios.get(`${apiUrl}/api/admin/students/${id}?nocache=${Date.now()}`, {
          headers: { Authorization: `Bearer ${sessionData.token}` }
        });
        if (response.data.success) {
          const student = response.data.student;
          setFormData({
            ...student,
            password: '',
            decryptedPassword: student.password || ''
          });
        } else {
          setApiError(response.data.message || 'Failed to load student data');
        }
      } catch (error) {
        console.error('Error fetching student:', error);
        setApiError(error.response?.data?.message || 'Error fetching student data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStudent();
  }, [id, navigate]);
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
        if (!sessionData || !sessionData.token) return;
        const response = await axios.get(`${apiUrl}/api/admin/courses?nocache=${Date.now()}`, {
          headers: { Authorization: `Bearer ${sessionData.token}` }
        });
        if (response.data.success) {
          setCourses(response.data.courses);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };
    fetchCourses();
  }, []);
  useEffect(() => {
    if (formData.preferableCourse) {
      const fetchBatches = async () => {
        try {
          const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
          if (!sessionData || !sessionData.token) return;
          const response = await axios.get(`${apiUrl}/api/admin/batches/${formData.preferableCourse}?nocache=${Date.now()}`, {
            headers: { Authorization: `Bearer ${sessionData.token}` }
          });
          if (response.data.success) {
            setBatches(response.data.batches);
          }
        } catch (error) {
          console.error('Error fetching batches:', error);
        }
      };
      fetchBatches();
    } else {
      setBatches([]);
    }
  }, [formData.preferableCourse]);
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
      // Changing the course invalidates whatever batch was previously
      // selected (batches belong to one course each) — without this the
      // stale batch id stayed in state and got submitted alongside the new
      // course, producing a mismatched enrollment row.
      ...(id === 'preferableCourse' && value !== prev.preferableCourse ? { batch: '' } : {})
    }));
    setErrors((prev) => ({ ...prev, [id]: '' }));
    setApiError('');
  };
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'firstName', 'lastName', 'mobile', 'email', 'address'
    ];
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim().length === 0) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').trim()} is required`;
      }
    });
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.mobile && !/^\d{10,15}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10-15 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setApiError('');
    const submitData = { ...formData };
    if (!submitData.password || submitData.password.trim() === '') {
      delete submitData.password;
    }
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;
      const response = await axios.put(`${apiUrl}/api/admin/students/${id}`, submitData, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setSuccessMessage('Student updated successfully');
        if (response.data.allocationWarning) {
          setAllocationNotice(response.data.allocationWarning);
          setTimeout(() => navigate('/admin-view-students'), 5000);
        } else {
          setTimeout(() => navigate('/admin-view-students'), 2000);
        }
      } else {
        setApiError(response.data.message || 'Failed to update student');
      }
    } catch (error) {
      console.error('Error updating student:', error);
      setApiError(error.response?.data?.message || 'Error updating student. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Edit Student</span>
                </li>
              </ul>
            </div>
            <div className="flex-align justify-content-end gap-8">
              <button
                type="submit"
                className="btn btn-main rounded-pill py-9"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Student'}
              </button>
            </div>
          </div>
          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-align gap-8">
              <h5 className="mb-0">Edit Student</h5>
            </div>
            <div className="card-body">
              {isLoading && <div className="alert alert-info">Loading...</div>}
              {apiError && <div className="alert alert-danger">{apiError}</div>}
              {successMessage && <div className="alert alert-success">{successMessage}</div>}
              {allocationNotice && <div className="alert alert-warning">{allocationNotice}</div>}
              <form onSubmit={handleSubmit}>
                <div className="row gy-20">
                  <div className="col-xxl-12">
                    <h6 className="mb-20 fw-semibold font-heading">Student Information</h6>
                    <div className="row g-20">
                      <div className="col-sm-6">
                        <label htmlFor="studentid" className="h5 mb-8 fw-semibold font-heading">
                          Student ID
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="studentid"
                          value={formData.studentid}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                        {errors.studentid && <div className="text-danger text-13">{errors.studentid}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="firstName" className="h5 mb-8 fw-semibold font-heading">
                          First Name <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.firstName && <div className="text-danger text-13">{errors.firstName}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="lastName" className="h5 mb-8 fw-semibold font-heading">
                          Last Name <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.lastName && <div className="text-danger text-13">{errors.lastName}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="aadharNo" className="h5 mb-8 fw-semibold font-heading">
                          Aadhar No./Emirates ID
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="aadharNo"
                          value={formData.aadharNo}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                        {errors.aadharNo && <div className="text-danger text-13">{errors.aadharNo}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="mobile" className="h5 mb-8 fw-semibold font-heading">
                          Mobile <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control py-11"
                          id="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.mobile && <div className="text-danger text-13">{errors.mobile}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="email" className="h5 mb-8 fw-semibold font-heading">
                          Email <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control py-11"
                          id="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.email && <div className="text-danger text-13">{errors.email}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="address" className="h5 mb-8 fw-semibold font-heading">
                          Address <span className="text-13 text-red-500 fw-medium">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                          disabled={isLoading}
                        />
                        {errors.address && <div className="text-danger text-13">{errors.address}</div>}
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="postcode" className="h5 mb-8 fw-semibold font-heading">
                          Postcode
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="postcode"
                          value={formData.postcode}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="mainTelephone" className="h5 mb-8 fw-semibold font-heading">
                          Main Telephone No.
                        </label>
                        <input
                          type="tel"
                          className="form-control py-11"
                          id="mainTelephone"
                          value={formData.mainTelephone}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="degree" className="h5 mb-8 fw-semibold font-heading">
                          Degree
                        </label>
                        <select
                          id="degree"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.degree}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select an answer</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="engineering" className="h5 mb-8 fw-semibold font-heading">
                          Engineering
                        </label>
                        <select
                          id="engineering"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.engineering}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select an answer</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="currentStatus" className="h5 mb-8 fw-semibold font-heading">
                          Current Status
                        </label>
                        <select
                          id="currentStatus"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.currentStatus}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select status</option>
                          <option value="Employed">Employed</option>
                          <option value="Not Employed">Not Employed</option>
                          <option value="Student">Student</option>
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="status" className="h5 mb-8 fw-semibold font-heading">
                          Status
                        </label>
                        <select
                          id="status"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.status}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value={1}>Enabled</option>
                          <option value={0}>Disabled</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-xxl-12">
                    <div className="row g-20">
                      <div className="col-sm-6">
                        <label htmlFor="preferableCourse" className="h5 mb-8 fw-semibold font-heading">
                          Preferable Course
                        </label>
                        <select
                          id="preferableCourse"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.preferableCourse}
                          onChange={handleChange}
                          disabled={isLoading}
                        >
                          <option value="" disabled>Select a course</option>
                          {courses.map((course) => (
                            <option key={course.id} value={course.id}>
                              {course.coursename}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="batch" className="h5 mb-8 fw-semibold font-heading">
                          Batch
                        </label>
                        <select
                          id="batch"
                          className="form-select py-9 placeholder-13 text-15"
                          value={formData.batch}
                          onChange={handleChange}
                          disabled={isLoading || !formData.preferableCourse}
                        >
                          <option value="" disabled>Select a batch</option>
                          {batches.map((batch) => (
                            <option key={batch.id} value={batch.id}>
                              {batch.batchname}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="username" className="h5 mb-8 fw-semibold font-heading">
                          Username
                        </label>
                        <input
                          type="text"
                          className="form-control py-11"
                          id="username"
                          value={formData.username}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                      <div className="col-sm-6">
                        <label htmlFor="password" className="h5 mb-8 fw-semibold font-heading">
                          Password (Leave blank to keep current)
                        </label>
                        <input
                          type="password"
                          className="form-control py-11"
                          id="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder={formData.decryptedPassword ? '********' : ''}
                          disabled={isLoading}
                        />
                        {formData.decryptedPassword && (
                          <small className="text-gray-400">Current password: {formData.decryptedPassword}</small>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-align justify-content-end gap-8">
                    <Link to="/admin-view-students" className="btn btn-outline-main rounded-pill py-9">
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      className="btn btn-main rounded-pill py-9"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
export default AdminEditStudent;